import { BookingServiceClient, UserServiceClient } from 'fitbeat.api-clients';
import {
  IDisplayableOpenInviteDetails,
  UserProfile,
  UserWorkoutSession,
} from 'fitbeat.gym-core';
import React, { Component } from 'react';
import { translate } from 'react-i18next';
import {
  ActivityIndicator,
  SafeAreaView,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { Button, ButtonTypes } from '../../components';
import { getApiConfig } from '../../config';
import { InfluencerLearnMorePage } from '../../Influencer';
import { GoogleAnalytics } from '../../services/google-analytics';
import Theme from '../../theme';
import { FormError } from '../../ui';
import { AuthTokenBuilder } from '../../utils/authTokenBuilder';
import { Logger } from '../../utils/Logger';
import { defaultScale } from '../../utils/scaling';
import { BookingFlowFooter } from '../components';
import { OpenInviteHeader } from '../steps/OpenInviteHeader';
import { InviteSettingsSelection } from './InviteSettingsSelection';

interface IProps {
  theme: 'light' | 'dark';
  shouldAcceptWorkout: boolean;
  workoutInviteId: string;
  footerStyle: StyleProp<ViewStyle>;
  userProfile: UserProfile;
  t(key: string, options?: any): string;
  onCompleteAcceptInvite(userWorkoutSession: UserWorkoutSession): void;
  ignoreInvite(): void;
  closeModal(): void;
}

interface IState {
  isLoading: boolean;
  avatar: any;
  error: Error | null;
  openingInviteDetails: IDisplayableOpenInviteDetails | null;
  showLearnMore: boolean;
  actionButtonLoading: WorkoutSettingsChoice | null;
}

export enum WorkoutSettingsChoice {
  PersonalSettings = 'PersonalSettings',
  InviterSettings = 'InviterSettings',
}

class OpenInviteComponent extends Component<IProps, IState> {
  private bookingServiceClient = new BookingServiceClient(
    getApiConfig(),
    fetch,
    new AuthTokenBuilder(),
  );
  private userServiceClient = new UserServiceClient(
    getApiConfig(),
    fetch,
    Logger.debug,
    new AuthTokenBuilder(),
    new GoogleAnalytics(),
  );
  constructor(props: IProps) {
    super(props);

    this.state = {
      isLoading: true,
      error: null,
      openingInviteDetails: null,
      showLearnMore: false,
      actionButtonLoading: null,
      avatar: {},
    };
  }

  public componentDidMount = async () => {
    try {
      const { workoutInviteId } = this.props;
      this.setState({ isLoading: true });
      const openingInviteDetails: IDisplayableOpenInviteDetails =
        await this.bookingServiceClient.getOpenInviteWorkoutDetails(
          workoutInviteId,
        );
      const avatar = await this.userServiceClient.getOtherUsersBase64Avatar(
        openingInviteDetails.referredByUserId,
      );
      this.setState({
        openingInviteDetails,
        isLoading: false,
        avatar: avatar.base64Avatar,
        showLearnMore: !!openingInviteDetails.isStreamingWorkout,
      });
    } catch (error) {
      this.setState({ error: error as Error, isLoading: false });
    }
  }

  public toggleShowLearnMore = () => {
    this.setState((prevState) => {
      return { showLearnMore: !prevState.showLearnMore };
    });
  }

  public acceptOpenInvite = async (workoutSettingsChoice: WorkoutSettingsChoice) => {
    try {
      const {
        workoutInviteId,
        onCompleteAcceptInvite,
      } = this.props;
      const useInviterSettings =
        workoutSettingsChoice === WorkoutSettingsChoice.InviterSettings;
      this.setState({ actionButtonLoading: workoutSettingsChoice });
      let userWorkoutSession;
      userWorkoutSession = await this.bookingServiceClient.acceptOpenInvite(workoutInviteId, useInviterSettings);
      onCompleteAcceptInvite(userWorkoutSession);
      this.setState({ actionButtonLoading: null });
    } catch (error) {
      this.setState({ error: error as Error, actionButtonLoading: null });
    }
  }

  public ignoreOpenInvite = async () => {
    const { ignoreInvite } = this.props;
    ignoreInvite();
  }

  public renderInviteDetailsError = () => {
    const { t, theme } = this.props;
    const { isLoading, openingInviteDetails, error } = this.state;
    const errorToDisplay = openingInviteDetails
      ? new Error(openingInviteDetails.workoutInviteError)
      : error;

    return (
      <>
        <FormError
          color={theme === 'light' ? Theme.CHARCOAL : Theme.WHITE}
          error={errorToDisplay}
        />
        <Button
          fontColor={Theme.WHITE}
          isLoading={isLoading}
          title={t('okay')}
          onPress={this.ignoreOpenInvite}
        />
      </>
    );
  }

  public renderAcceptOrIgnoreWithApiError = () => {
    const { t, theme } = this.props;
    const { isLoading, error, actionButtonLoading } = this.state;
    return (
      <>
        <FormError
          color={theme === 'light' ? Theme.CHARCOAL : Theme.WHITE}
          error={error}
        />
        <Button
          buttonType={ButtonTypes.OUTLINE}
          fontColor={Theme.PRIMARY}
          isLoading={isLoading}
          title={t('ignoreInvite')}
          onPress={this.ignoreOpenInvite}
          disabled={actionButtonLoading}
        />
      </>
    );
  }

  public render() {
    const { t, footerStyle, closeModal, userProfile } = this.props;
    const {
      openingInviteDetails,
      isLoading,
      error,
      showLearnMore,
      avatar,
      actionButtonLoading,
    } = this.state;
    const inviterName = openingInviteDetails?.referredByFirstname;

    return (
      <View style={{ flex: 1 }}>
        {showLearnMore && (
          <InfluencerLearnMorePage
            close={closeModal}
            goBack={this.toggleShowLearnMore}
            avatar={avatar}
            inviteDetails={openingInviteDetails}
            loading={!!actionButtonLoading}
            actionButton={this.learnMoreButtonAction()}
            error={error}
          />
        )}
        {!showLearnMore && (
          <>
            <View style={{ flex: 1 }}>
              {openingInviteDetails && (
                <>
                  <OpenInviteHeader
                    inviterId={openingInviteDetails.referredByUserId}
                    inviterName={openingInviteDetails.referredByFirstname}
                    workoutInviteStartTime={
                      openingInviteDetails.workoutInviteStartTime
                    }
                    workoutInviteDuration={
                      openingInviteDetails.workoutInviteDuration
                    }
                    closeModal={closeModal}
                    avatar={avatar}
                    toggleShowLearnMore={this.toggleShowLearnMore}
                    isStreamingWorkout={openingInviteDetails.isStreamingWorkout}
                  />
                  <View style={styles.selectionTilesContainer}>
                    <InviteSettingsSelection
                      t={t}
                      heading={t('joinWithInviterSettings', { inviterName })}
                      description={t('invitersWorkoutDescription', {
                        inviter: openingInviteDetails.referredByFirstname,
                      })}
                      workoutSettings={openingInviteDetails.workoutSettings}
                      actionButton={
                        <Button
                          title={t('joinWithInviterSettingsActionButton', { inviterName })}
                          fontColor={Theme.WHITE}
                          onPress={() => this.acceptOpenInvite(WorkoutSettingsChoice.InviterSettings)}
                          containerStyle={styles.actionButton}
                          isLoading={actionButtonLoading === WorkoutSettingsChoice.InviterSettings}
                          disabled={actionButtonLoading}
                        />
                      }
                    />
                    <InviteSettingsSelection
                      t={t}
                      heading={t('joinInviterWithMySettings', { inviterName })}
                      description={t('myPersonalWorkoutDescription')}
                      userProfile={userProfile}
                      actionButton={
                        <Button
                          title={t('joinWithMySettingsActionButton')}
                          fontColor={Theme.WHITE}
                          containerStyle={[styles.actionButton, { marginTop: defaultScale(15) }]}
                          onPress={() => this.acceptOpenInvite(WorkoutSettingsChoice.PersonalSettings)}
                          isLoading={actionButtonLoading === WorkoutSettingsChoice.PersonalSettings}
                          disabled={actionButtonLoading}
                        />
                      }
                    />
                  </View>
                </>
              )}
            </View>
            <SafeAreaView style={styles.footer}>
                <BookingFlowFooter style={footerStyle}>
                  {isLoading ? (
                    <ActivityIndicator color={Theme.PRIMARY} />
                  ) : (openingInviteDetails &&
                      openingInviteDetails.workoutInviteError) ||
                    error ? (
                    this.renderInviteDetailsError()
                  ) : (
                    this.renderAcceptOrIgnoreWithApiError()
                  )}
                </BookingFlowFooter>
            </SafeAreaView>
          </>
        )}
      </View>
    );
  }

  private learnMoreButtonAction(): () => void {
    const livestreamOptions = this.state.openingInviteDetails?.livestreamOptions;
    const closeLearnMore = () => this.setState({ showLearnMore: false });
    const acceptUsingMySettings = () => this.acceptOpenInvite(WorkoutSettingsChoice.PersonalSettings);
    if (livestreamOptions?.streamerWillDropOffAfterTalk) {
      return acceptUsingMySettings;
    }
    return closeLearnMore;
  }
}

const styles = StyleSheet.create({
  footer: {
    justifyContent: 'flex-end',
    margin: defaultScale(15),
  },
  selectionTilesContainer: {
    flex: 1,
    padding: defaultScale(12),
  },
  actionButton: {
    marginHorizontal: defaultScale(10),
  },
});

export const OpenInvite = translate(['bookings'], { wait: true })(
  OpenInviteComponent,
);
